// Voice usage — display math + read of the gateway's read-only /voice-usage
// snapshot. Enforcement always lives server-side (tutor-token / convo-token /
// *-heartbeat); this only powers the remaining-minutes UI.
//
// Caps mirror baba-v2/mobile/constants/paywall.ts exactly (TUTOR_*, CONVO_*).
// Keep these three in lockstep: mobile paywall.ts, the Worker token/heartbeat
// handlers, and this file.
import { getAuthToken } from '@/lib/baba-auth'
import type { VoiceTier, VoiceUsageInfo, VoiceUsageRow } from './types'

// ── Limits (seconds) — mirror mobile/constants/paywall.ts ──────────────────
export const TUTOR_FREE_LIFETIME_SECONDS = 0 // Pro+ only
export const TUTOR_PRO_PLUS_SECONDS = 60 * 60 // 60 min / 30-day rolling period

export const CONVO_FREE_TRIAL_SECONDS = 0
export const CONVO_PRO_SECONDS = 15 * 60 // 15 min / 30-day rolling period
export const CONVO_PRO_PLUS_SECONDS = 60 * 60 // 60 min / 30-day rolling period

/** Grace window past subscription_expires_at before a stale paid row is
 *  treated as lapsed (webhook/sync lag). Mirror: mobile paywall.ts + the
 *  Worker token/heartbeat handlers (ENTITLEMENT_GRACE_MS). */
export const ENTITLEMENT_GRACE_MS = 3 * 24 * 60 * 60 * 1000

function resolveTier(row: Pick<VoiceUsageRow, 'subscription_tier'> | null): VoiceTier {
  const tier = row?.subscription_tier
  return tier === 'pro' || tier === 'pro_plus' ? tier : 'free'
}

function isSubActive(
  row: Pick<VoiceUsageRow, 'subscription_tier' | 'subscription_expires_at'> | null,
  nowMs: number,
): boolean {
  const tier = resolveTier(row)
  if (tier === 'free') return false
  if (!row?.subscription_expires_at) return true
  return new Date(row.subscription_expires_at).getTime() + ENTITLEMENT_GRACE_MS > nowMs
}

/** Tutor is Pro+ only — free AND Pro both get the 0-second (locked) budget. */
export function computeTutorUsage(row: VoiceUsageRow | null, nowMs: number = Date.now()): VoiceUsageInfo {
  const tier = resolveTier(row)
  const active = isSubActive(row, nowMs)

  if (tier === 'pro_plus' && active) {
    const resetMs = row?.tutor_session_reset_date ? new Date(row.tutor_session_reset_date).getTime() : 0
    const used = resetMs > 0 && resetMs <= nowMs ? 0 : (row?.tutor_session_seconds ?? 0)
    const remaining = Math.max(0, TUTOR_PRO_PLUS_SECONDS - used)
    return {
      usedSeconds: used,
      limitSeconds: TUTOR_PRO_PLUS_SECONDS,
      remainingSeconds: remaining,
      resetDate: resetMs > 0 ? new Date(resetMs) : null,
      tier,
      isLifetimeTrial: false,
      exhausted: remaining <= 0,
    }
  }

  // Free and Pro: a single lifetime preview that never resets (currently 0 —
  // hard paywall). Kept as a real computation (not hardcoded "exhausted") so
  // a future re-enable of the preview only needs the constant flipped.
  const used = row?.tutor_trial_seconds_used ?? 0
  const remaining = Math.max(0, TUTOR_FREE_LIFETIME_SECONDS - used)
  return {
    usedSeconds: used,
    limitSeconds: TUTOR_FREE_LIFETIME_SECONDS,
    remainingSeconds: remaining,
    resetDate: null,
    tier,
    isLifetimeTrial: true,
    exhausted: remaining <= 0,
  }
}

/** Convo: free = 0 (locked), Pro = 15 min/period, Pro+ = 60 min/period. */
export function computeConvoUsage(row: VoiceUsageRow | null, nowMs: number = Date.now()): VoiceUsageInfo {
  const tier = resolveTier(row)
  const active = isSubActive(row, nowMs)

  if (active && (tier === 'pro' || tier === 'pro_plus')) {
    const limitSeconds = tier === 'pro_plus' ? CONVO_PRO_PLUS_SECONDS : CONVO_PRO_SECONDS
    const resetMs = row?.convo_session_reset_date ? new Date(row.convo_session_reset_date).getTime() : 0
    const used = resetMs > 0 && resetMs <= nowMs ? 0 : (row?.convo_session_seconds ?? 0)
    const remaining = Math.max(0, limitSeconds - used)
    return {
      usedSeconds: used,
      limitSeconds,
      remainingSeconds: remaining,
      resetDate: resetMs > 0 ? new Date(resetMs) : null,
      tier,
      isLifetimeTrial: false,
      exhausted: remaining <= 0,
    }
  }

  const used = row?.convo_trial_seconds_used ?? 0
  const remaining = Math.max(0, CONVO_FREE_TRIAL_SECONDS - used)
  return {
    usedSeconds: used,
    limitSeconds: CONVO_FREE_TRIAL_SECONDS,
    remainingSeconds: remaining,
    resetDate: null,
    tier,
    isLifetimeTrial: true,
    exhausted: remaining <= 0,
  }
}

/** `mm:ss` for a countdown/remaining display. */
export function formatVoiceSeconds(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

// ── Base URL ─────────────────────────────────────────────────────────────
// The Worker serves /convo-token, /tutor-token, /convo-heartbeat,
// /tutor-heartbeat, /voice-usage at ROOT paths (baba-v2/cloudflare-worker/src
// /index.ts), on the SAME host as the gateway's /api/v1/* routes but without
// that prefix. Mirrors mobile's VOICE_BASE_URL (services/supabase.ts).
function normalizeBaseUrl(v: string | undefined): string {
  if (!v) return ''
  return v.trim().replace(/^["']|["']$/g, '').replace(/\/+$/, '')
}

const envVoiceUrl =
  typeof import.meta !== 'undefined' && import.meta.env
    ? normalizeBaseUrl(import.meta.env.VITE_BABA_VOICE_URL as string | undefined)
    : ''

export const VOICE_BASE_URL = envVoiceUrl || 'https://translate.itsbaba.com'

export class VoiceFetchError extends Error {
  readonly status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'VoiceFetchError'
    this.status = status
  }
}

/** Read-only usage snapshot for the current signed-in user. Never throws for
 *  a signed-out/never-provisioned caller — resolves null so callers fall
 *  back to the free-tier defaults. */
export async function fetchVoiceUsageRow(): Promise<VoiceUsageRow | null> {
  const token = getAuthToken()
  if (!token) return null
  try {
    const res = await fetch(`${VOICE_BASE_URL}/voice-usage`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!res.ok) return null
    const data = (await res.json()) as VoiceUsageRow & { found?: boolean }
    if (!data?.found) return null
    return data
  } catch {
    return null
  }
}
